import React from "react";
import { useDispatch } from "react-redux";
import jsPDF from "jspdf";

import FileIcon from "@material-ui/icons/InsertDriveFile";
import DeleteIcon from "@material-ui/icons/Cancel";

import Chip from "@material-ui/core/Chip";

import { showNotification } from "jazasoft";
import { regex } from "@jazasoft/mui-table";

import { toFixed, isTrue, getDistinctValues, isEmpty, getWeightedMean, sizeKey as getSizeKey, listFromCsv } from "../../../utils/helpers";
import { cbPartSort, getSizeRatio } from "../../../helpers/orderHelper";
import { REGEX_REPEAT, REGEX_SHRINKAGE } from "../../../utils/regex";
import { FabricDirection, Role, EstimationState, CSV } from "../../../utils/types";
import useQuery from "../../../hooks/useQuery";
import useMutation from "../../../hooks/useMutation";
import hasRole from "../../../utils/hasRole";
import { format } from "../../../utils/dateUtils";
import { useDeepMemo, useSetting, useTenant, useTime } from "../../../hooks";
import { minReqHeight, setDefault } from "../../../helpers/pdfHelper";
import SettingKeys from "../../../utils/SettingKeys";
import { CUT_OFF_SIZE_RATIO, PART_SHELL } from "../../../constants";

const getSummaryRow = (costing, label, part, value = () => {}) => {
  let row = { parameter: label };
  const revisions = getDistinctValues(costing?.parts?.map(p => p.revisionNumber))?.sort((a, b) => a - b);
  for (let revIdx = 1; revIdx <= revisions.length; revIdx++) {
    const rev = revisions[revIdx - 1];
    const item = costing.parts?.find(e => e.revisionNumber === rev && e.part === part);
    row[`revision${rev}`] = value(item) || "-";
  }
  return row;
};

const topSummaryParameters = ["costingCu,Costing CU", "markerEfficiency,Marker Efficiency", "maxWay,Max Way"];
const bottomSummaryParameters = ["reason,Reason", "remarks,Remarks", "createdAt,Date", "finalizedOption,Finalized Option"];

const parseSizeRatio = sizeBreakup => {
  return Object.keys(sizeBreakup)
    .map(key => ({ size: key, ...sizeBreakup[key] }))
    .sort((a, b) => a.serial - b.serial)
    .map(e => `${e.size}:${e.qty}`)
    .join(", ");
};

const latestValue = fitValue => {
  if (!fitValue) return;
  const fit = JSON.parse(fitValue);
  const lastestRevision = Object.keys(fit)
    .map(key => parseInt(key))
    .sort((a, b) => b - a)[0];
  return fit[lastestRevision];
};

const parseMarker = (m, sizeKeys, idx) => ({
  ...m,
  markerNo: ++idx,
  name: sizeKeys
    .filter(sKey => m[getSizeKey(sKey)] !== "" && parseInt(m[getSizeKey(sKey)]) > 0)
    .map(sKey => `${sKey}:${m[getSizeKey(sKey)]}`)
    .join(", "),
  way: sizeKeys
    .filter(size => m[getSizeKey(size)] !== "" && parseInt(m[getSizeKey(size)]) > 0)
    .reduce((acc, size) => acc + parseInt(m[getSizeKey(size)]), 0)
});

const sanitizeCosting = costing => {
  const { sizeBreakupValue, ...rest } = costing || {};
  return rest;
};

const useCostingView = ({ id, roles }) => {
  const [version, setVersion] = React.useState(1);
  const [revTabIdx, setRevTabIdx] = React.useState(0);
  const [partIdx, setPartIdx] = React.useState(0);
  const [costing, setCosting] = React.useState({});
  const [editing, setEditing] = React.useState(false);
  const [reasonEditing, setReasonEditing] = React.useState(false);
  const [openFinalizeDialog, setOpenFinalizeDialog] = React.useState(false);
  const [isAttachmentUpload, setIsAttachmentUpload] = React.useState(false);
  const [drawerState, setDrawerState] = React.useState({ open: false, rows: [], columns: [] });
  const [dialogState, setDialogState] = React.useState({ active: false, action: "history", data: {}, type: null });

  const dispatch = useDispatch();
  const { utc_offset } = useTime();
  const tenant = useTenant();
  const isEstCad = hasRole(roles, [Role.EST_CAD]);

  const costingReasonsStr = useSetting(SettingKeys.COSTING_REASONS);
  const costingReasons = listFromCsv(costingReasonsStr);
  const choicesCostingReasons = costingReasons.map(e => ({ id: e, name: e }));

  const { data: costingData = {}, loading: cLoading } = useQuery({ url: `costings/${id}`, version });
  const { data: fabricPatternList = [] } = useQuery({ url: "fabric-patterns", params: { size: 100 } }, ({ id, name } = {}) => ({
    id: `${id}`,
    name
  }));
  const { data: cuChecklist } = useQuery(costing && costing?.cuChecklistId && { url: `cuChecklists/${costingData?.cuChecklistId}` });

  const techSheets = cuChecklist?.techSheets || "";
  const choicesTechSheets = techSheets ? techSheets.split(",").map(e => ({ id: e, name: e })) : [];

  const [mutate, { loading: loading1 }] = useMutation();
  const [download, { loading: loading2 }] = useMutation({ rawResponse: true });

  React.useEffect(() => {
    setCosting({
      ...costingData,
      fitValue: latestValue(costingData.fit),
      techSheetList: costingData?.techSheets?.trim().length > 0 ? costingData?.techSheets?.trim().split(",") : []
    });
  }, [cLoading, costingData]);

  const revisions = getDistinctValues(costing?.parts?.map(p => p.revisionNumber))?.sort((a, b) => a - b) || [];
  const revisionCount = revisions?.length || 1;

  const manuallyFinalizedRevNo =
    costing?.parts?.find(p => !isEmpty(p.manualFinalizedOption) && isTrue(p.manualFinalizedOption))?.revisionNumber || undefined;
  const choicesRevisions = revisions.map(revNo => ({ id: revNo, name: `Costing ${revNo}` }));

  const busy = loading1 || loading2 || cLoading;
  const isActiveRevision = isEmpty(revisions) ? false : revisions[revTabIdx] === revisions[revisions?.length - 1];
  const isAdminOrCAD = hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER, Role.CUT_PLANNER]);

  const onDetailsSubmit = values => {
    const options = {
      url: `costings/${id}`,
      method: "put",
      params: { revNo: revisions[revTabIdx] },
      data: {
        ...costing,
        remarks: values.remarks,
        fitValue: values.fitValue,
        techSheets: values?.techSheetList?.join(",")
      }
    };
    mutate(options, {
      onSuccess: data => {
        setCosting({
          ...data,
          fitValue: latestValue(data.fit),
          techSheetList: data?.techSheets?.trim().length > 0 ? data?.techSheets?.trim().split(",") : []
        });
        setEditing(false);
        dispatch(showNotification("Style details update successfully."));
      }
    });
  };

  const onRevisionCreate = () => setReasonEditing(true);
  const onReasonEditingClose = () => setReasonEditing(false);
  const onFinalizeDialogClose = () => setOpenFinalizeDialog(false);

  const onCostingReasonsSubmit = values => {
    if (isEmpty(values?.reason)) {
      dispatch(showNotification(`Reason is required for creating new Costing`, { type: "danger" }));
      return;
    }
    if (values?.reason === "Others" && isEmpty(values?.remarks)) {
      dispatch(showNotification(`Remarks is required if reason is selected as Others.`, { type: "danger" }));
      return;
    }

    const options = {
      url: `costings/${id}`,
      method: "put",
      params: { action: "revise" },
      data: { ...costing, reason: values?.reason, reasonRemarks: values?.remarks }
    };
    mutate(options, {
      onSuccess: () => {
        setVersion(version => version + 1);
        onReasonEditingClose();
        dispatch(showNotification("New Revision created successfully."));
      }
    });
  };

  const columnsPart = [
    { dataKey: "part", title: "Part" },
    {
      dataKey: "placement",
      title: "Fab. Placement",
      length: 20,
      inputType: "text-input",
      disabled: () => costing?.state === EstimationState.COMPLETED || costing?.state === EstimationState.AVAILABLE || isEstCad
    },
    {
      dataKey: "fabricPatternId",
      title: "Fab. Pattern",
      render: value => fabricPatternList.find(e => `${e.id}` === `${value}`)?.name,
      inputType: "select-input",
      choices: fabricPatternList,
      disabled: () => costing?.state === EstimationState.COMPLETED || costing?.state === EstimationState.AVAILABLE || isEstCad
    },
    {
      dataKey: "width",
      title: "Width (CM)",
      inputType: "text-input",
      disabled: () => costing?.state === EstimationState.COMPLETED || costing?.state === EstimationState.AVAILABLE || isEstCad,
      options: { type: "number" }
    },
    {
      dataKey: "repeat",
      title: "Repeat",
      inputType: "text-input",
      disabled: () => costing?.state === EstimationState.COMPLETED || costing?.state === EstimationState.AVAILABLE || isEstCad,
      validate: regex(REGEX_REPEAT, "Invalid format. E.g - 1.5 X 2.5 or 1.5")
    },
    {
      dataKey: "shrinkage",
      title: "Shrinkage",
      inputType: "text-input",
      disabled: () => costing?.state === EstimationState.COMPLETED || costing?.state === EstimationState.AVAILABLE || isEstCad,
      validate: regex(REGEX_SHRINKAGE, "Invalid format. E.g - L1.5% W2.5%")
    },
    {
      dataKey: "fabDirection",
      title: "Fab. Direction",
      inputType: "select-input",
      choices: Object.keys(FabricDirection).map(key => ({ id: FabricDirection[key], name: FabricDirection[key] })),
      disabled: () => costing?.state === EstimationState.COMPLETED || costing?.state === EstimationState.AVAILABLE || isEstCad
    },
    {
      dataKey: "matchingParameter",
      title: "Matching Parameter",
      inputType: "text-input",
      disabled: () => costing?.state === EstimationState.COMPLETED || costing?.state === EstimationState.AVAILABLE || isEstCad
    },
    {
      dataKey: "csv",
      title: "CSV",
      inputType: "select-input",
      choices: Object.values(CSV).map(e => ({ id: e, name: e })),
      disabled: () => costing?.state === EstimationState.COMPLETED || costing?.state === EstimationState.AVAILABLE || isEstCad
    },
    {
      dataKey: "layCu",
      title: "Lay CU",
      render: value => (value ? toFixed(value, 3) : "-")
    },
    {
      dataKey: "wastage",
      title: "Wastage (%)",
      render: value => (!isEmpty(value) ? toFixed(value, 1) : "-"),
      inputType: "text-input",
      disabled: () => !hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER, Role.MERCHANT, Role.EST_CAD, Role.CUT_PLANNER]),
      options: { type: "number" }
    },
    {
      dataKey: "costingCu",
      title: "Costing CU",
      render: value => (value ? toFixed(value, 3) : "-")
    }
  ];

  const rowsPart =
    costing.parts
      ?.filter(p => p.revisionNumber === revisions[revTabIdx])
      .sort(cbPartSort)
      .map(e => ({
        ...e,
        fabricPatternId: e.fabricPattern?.id,
        layCu: toFixed(e.layCu, 3),
        costingCu: toFixed(e.costingCu, 3)
      })) || [];

  const partList = rowsPart.map(p => p.part);

  const onPartSubmit = (parts, form, onSubmitComplete) => {
    let restParts = costing.parts.filter(p => p.revisionNumber !== revisions[revTabIdx]);
    const updatedCosting = {
      ...costing,
      parts: [...restParts, ...parts]
    };

    const options = {
      url: `costings/${id}`,
      method: "put",
      params: { revNo: revisions[revTabIdx] },
      data: updatedCosting
    };
    mutate(options, {
      onSuccess: data => {
        setCosting({ ...data, fitValue: latestValue(data.fit) });
        const updatedParts = data?.parts
          ?.filter(p => p.revisionNumber === revisions[revTabIdx])
          .sort(cbPartSort)
          .map(e => ({ ...e, fabricTypeId: e.fabricType?.id, layCu: toFixed(e.layCu, 3) }));
        onSubmitComplete(updatedParts);
      },
      onFailure: () => onSubmitComplete()
    });
  };

  //// Process Size Breakup data ////
  const sizeBreakupList = JSON.parse(costing?.sizeBreakup || "[]");
  const activeRevNo = revisions[revTabIdx];
  const sbList = sizeBreakupList.filter(sb => `${sb.revisionNumber}` === `${activeRevNo}`);
  const sizeKeys = getDistinctValues(sbList.sort((a, b) => a.serial - b.serial).map(sb => sb.sizeKey));

  const columnsSizeBreakup = useDeepMemo(() => {
    const cols = [
      ...sizeKeys.map(sKey => ({
        dataKey: getSizeKey(sKey),
        title: sKey.split("_")[1],
        align: "center",
        inputType: "text-input",
        options: { type: "number" }
      })),
      {
        dataKey: "orderQty",
        title: "Order Qty",
        align: "center",
        inputType: "text-input",
        options: { type: "number" }
      }
    ];

    return cols;
  }, [sizeKeys]);

  const rowsSizeBreakup = useDeepMemo(() => {
    const noOfColors = costing?.colors?.split(",")?.length || 1;
    const isSizeRatio = !isEmpty(sbList) && sbList.every(sb => sb.qty <= CUT_OFF_SIZE_RATIO);

    return [
      sbList.reduce(
        (acc, sb) => ({
          ...acc,
          [getSizeKey(sb.sizeKey)]: isSizeRatio ? sb.qty : sb.qty * noOfColors
        }),
        {
          orderQty: costing?.orderQty || 0
        }
      )
    ];
  }, [costing, sbList]);

  const onSizeBreakupSubmit = (rows, form, onSubmitComplete) => {
    const updatedSbList = sizeKeys.map((sKey, idx) => ({
      serial: idx + 1,
      sizeGroup: sKey.split("_")[0],
      size: sKey.split("_")[1],
      qty: rows[0][getSizeKey(sKey)] || 0
    }));

    const options = {
      url: `costings/${id}`,
      method: "put",
      params: { revNo: revisions[revTabIdx] },
      data: { ...costing, orderQty: rows[0].orderQty, sizeBreakupValue: JSON.stringify(updatedSbList) }
    };

    mutate(options, {
      onSuccess: data => {
        setCosting({ ...data, fitValue: latestValue(data.fit) });
        onSubmitComplete(rows);
      },
      onFailure: () => onSubmitComplete()
    });
  };

  const onDrawerSubmit = (updatedSizeBreakupList, orderQty, onActionComplete) => {
    const revNo = revisions[revTabIdx];

    const currentRevisionSizeBreakupList = updatedSizeBreakupList.map((sb, idx) => ({
      serial: idx + 1,
      sizeGroup: sb.sizeGroup || "G1",
      size: sb.size,
      qty: Number(sb.qty) || 0
    }));

    const options = {
      url: `costings/${id}`,
      method: "put",
      params: { revNo },
      data: {
        ...sanitizeCosting(costing),
        orderQty,
        sizeBreakupValue: JSON.stringify(currentRevisionSizeBreakupList)
      }
    };

    mutate(options, {
      onSuccess: data => {
        setCosting({
          ...sanitizeCosting(data),
          fitValue: latestValue(data.fit),
          techSheetList: data?.techSheets?.trim().length > 0 ? data?.techSheets?.trim().split(",") : []
        });
        setVersion(version => version + 1);
        dispatch(showNotification("Size Breakup updated successfully."));
        onActionComplete && onActionComplete([]);
        setDrawerState({ open: false, rows: [], columns: [] });
      },
      onFailure: () => {
        onActionComplete && onActionComplete([]);
        setDrawerState({ open: false, rows: [], columns: [] });
      }
    });
  };

  //// Process Marker data ////
  const markers = costing?.markers?.filter(m => `${m.revisionNumber}` === `${revisions[revTabIdx]}`) || [];

  const columnsMarker = useDeepMemo(() => {
    const cols = [
      { dataKey: "markerNo", title: "Marker No." },
      ...sizeKeys.map(sKey => ({
        dataKey: getSizeKey(sKey),
        title: sKey.split("_")[1],
        inputType: "text-input",
        options: { type: "number" }
      })),
      { dataKey: "way", title: "Way" },
      { dataKey: "qtyPercent", title: "Qty (%)", inputType: "text-input", options: { type: "number" } },
      { dataKey: "length", title: "Length", inputType: "text-input", options: { type: "number" } },
      { dataKey: "efficiency", title: "Efficiency", inputType: "text-input", options: { type: "number" } },
      { dataKey: "remarks", title: "Remarks", inputType: "text-input" },
      {
        dataKey: "miniMarker",
        title: "Mini Marker",
        render: (filename, shortValue, row) =>
          filename ? (
            <Chip
              label={filename}
              icon={<FileIcon />}
              variant="outlined"
              onClick={() => onDownloadMiniMarker({ filename, revNo: row.revisionNumber, part: row.part })}
              deleteIcon={isActiveRevision && isAdminOrCAD ? <DeleteIcon /> : <React.Fragment />}
              onDelete={() =>
                isAdminOrCAD &&
                setDialogState({
                  action: "miniMarkerDelete",
                  active: true,
                  data: { filename, revNo: row.revisionNumber, part: row.part }
                })
              }
            />
          ) : (
            ""
          )
      }
    ];

    return cols;
  }, [sizeKeys]);

  const attachColumnsMarker = [
    ...columnsMarker.filter(c => c.dataKey !== "miniMarker"),
    {
      dataKey: "attachment",
      title: "Attachment",
      inputType: "file-input",
      options: { accept: { "*/*": [] } }
    }
  ].map(column => (column.dataKey !== "attachment" ? { ...column, inputType: "text-field" } : { ...column }));

  const rowsMarker = useDeepMemo(() => {
    return markers
      .sort((a, b) => a.markerNo - b.markerNo)
      .map((marker, idx) => {
        const sizeRatio = getSizeRatio(marker.name, sizeKeys, getSizeKey);
        return { ...marker, markerNo: `M${marker.markerNo || idx + 1}`, ...sizeRatio };
      });
  }, [markers, sizeKeys]);

  const onMarkerSubmit = part => (markers, form, onSubmitComplete) => {
    const markerList = markers.map((m, idx) => parseMarker(m, sizeKeys, idx)).filter(m => m.name);

    if (!isAttachmentUpload) {
      const options = {
        url: `costings/${id}/markers`,
        method: "put",
        params: { revNo: revisions[revTabIdx], part },
        data: markerList
      };
      mutate(options, {
        onSuccess: data => {
          if (Array.isArray(data)) {
            onSubmitComplete(data.sort((a, b) => b?.way - a?.way).map((m, idx) => ({ ...m, markerNo: "M" + m.markerNo })));
            setVersion(version => version + 1);
          }
        },
        onFailure: () => onSubmitComplete()
      });
    } else if (isAttachmentUpload) {
      const markerList = markers.map(m => ({ id: m.id, miniMarker: m.attachment?.name }));

      const data = new FormData();
      markers.forEach(m => {
        if (!isEmpty(m.attachment)) {
          data.append("files", m.attachment);
        }
      });

      const options = {
        url: `costings/${id}/miniMarkers`,
        method: "post",
        data,
        params: { part, constingId: id, body: JSON.stringify(markerList) },
        headers: { "Content-Type": "multipart/form-data" }
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("Mini Markers uploaded successfully"));
          setVersion(version + 1);
          setIsAttachmentUpload(false);
        },
        onFailure: () => {
          setIsAttachmentUpload(false);
          onSubmitComplete && onSubmitComplete(markers);
        }
      });
    }
  };

  let columnsSummary = [];
  let rowsSummaryMap = {};

  if (revisionCount > 1) {
    columnsSummary = [
      { dataKey: "parameter", title: "Parameter", length: 32 },
      ...revisions.map(revision => ({ dataKey: `revision${revision}`, title: `Costing ${revision}` }))
    ];

    const parts = getDistinctValues(costing.parts?.sort(cbPartSort).map(p => p.part));

    rowsSummaryMap = parts.reduce((acc, part) => {
      let partRows = [];

      topSummaryParameters.forEach(e => {
        const key = e.split(",")[0];
        const label = e.split(",")[1];

        if (["markerEfficiency", "maxWay"]?.includes(key)) {
          let row = { parameter: label };
          revisions.forEach(rev => {
            const markers = costing?.markers?.filter(m => m.revisionNumber === rev && m.part === part);
            const wayMarkers = markers?.map(m => m.way).filter(e => !isEmpty(e));

            const markerEfficiency = getWeightedMean(
              markers
                .filter(m => !isEmpty(m.efficiency))
                .map(m => ({
                  key: Number(m.efficiency),
                  weight: !isEmpty(m.qtyPercent) ? Number(m.qtyPercent) : 100
                }))
            );

            row[`revision${rev}`] =
              key === "markerEfficiency"
                ? !isEmpty(markerEfficiency)
                  ? toFixed(markerEfficiency, 2)
                  : "-"
                : wayMarkers?.length > 0
                  ? Math.max(...wayMarkers)
                  : "-";
          });
          partRows.push(row);
        } else {
          const row = getSummaryRow(costing, label, part, item => (key.includes("Cu") ? toFixed(item[key], 3) : item[key]));
          partRows.push(row);
        }
      });

      const bodyParts = costing.parts?.filter(p => p.part === part);

      const fitMap = JSON.parse(costing.fit || "{}");
      let hasChanged = getDistinctValues(Object.keys(fitMap).map(key => fitMap[key])).length > 1;
      if (hasChanged) {
        const row = {
          parameter: "Fit",
          ...Object.keys(fitMap).reduce((acc, key) => ({ ...acc, [`revision${key}`]: fitMap[key] }), {})
        };
        partRows.push(row);
      }

      hasChanged = getDistinctValues(bodyParts.map(e => e.fabricType?.id)).length > 1;
      if (hasChanged) {
        const row = getSummaryRow(costing, "Fabric Type", part, item => item?.fabricType?.name);
        partRows.push(row);
      }

      const parameters = [
        "fabPlacement,Placement",
        "width,Width (CM)",
        "repeat,Repeat",
        "shrinkage,Shrinkage",
        "fabDirection,Fab. Direction",
        "matchingParameter,Matching Parameter",
        "csv,CSV"
      ];

      parameters.forEach(e => {
        const key = e.split(",")[0];
        const label = e.split(",")[1];
        hasChanged = getDistinctValues(bodyParts.map(e => e[key])).length > 1;
        if (hasChanged) {
          const row = getSummaryRow(costing, label, part, item => item[key]);
          partRows.push(row);
        }
      });

      const sbMap = JSON.parse(costing.sizeBreakup || "{}");
      hasChanged = getDistinctValues(Object.keys(sbMap).map(key => parseSizeRatio(sbMap[key]))).length > 1;
      if (hasChanged) {
        const row = {
          parameter: "Size Ratio",
          ...Object.keys(sbMap).reduce((acc, key) => ({ ...acc, [`revision${key}`]: parseSizeRatio(sbMap[key]) }), {})
        };
        partRows.push(row);
      }

      bottomSummaryParameters.forEach(e => {
        const key = e.split(",")[0];
        const label = e.split(",")[1];
        let mapper;

        if (key === "reason") {
          mapper = item => item?.reason || "-";
        } else if (key === "remarks") {
          mapper = item => item?.remarks || "-";
        } else if (key === "createdAt") {
          mapper = item => (!isEmpty(item[key]) ? format(item[key], utc_offset, "MMM dd, yy HH:mm aa") : "");
        } else if (key === "finalizedOption") {
          mapper = item =>
            !isEmpty(item?.manualFinalizedOption)
              ? isTrue(item?.manualFinalizedOption)
                ? "Selected"
                : "Not Selected"
              : isTrue(item?.systemFinalizedOption)
                ? "Selected"
                : "Not Selected";
        }

        const row = getSummaryRow(costing, label, part, mapper);
        partRows.push(row);
      });

      return {
        ...acc,
        [part]: partRows
      };
    }, {});
  }

  const onDownloadTechSheet = filename => {
    const options = {
      url: `estTechSheets`,
      method: "get",
      params: { filename, cuChecklistId: costingData?.cuChecklistId },
      responseType: "arraybuffer"
    };
    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          let blob = new Blob([response.data], { type: "octet/stream" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
      }
    });
  };

  const onFooterActionClick = part => (event, action, rows, filterValues, onActionComplete) => {
    if (action === "upload") {
      setIsAttachmentUpload(true);
      setVersion(version + 1);
      onActionComplete && onActionComplete(rows);
    }
    if (action === "cancel") {
      setIsAttachmentUpload(false);
      onActionComplete && onActionComplete(rows);
    }
  };

  const onDownloadMiniMarker = ({ filename, ...restParams }) => {
    const options = {
      url: `costings/${costing?.id}/miniMarkers`,
      method: "get",
      params: { filename, ...restParams },
      responseType: "arraybuffer"
    };
    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var file = header ? header.match(/filename="(.+)"/)[1] : filename;
          let blob = new Blob([response.data], { type: "octet/stream" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = file;
          link.click();
        }
      }
    });
  };

  const onDeleteMiniMarker = () => {
    const { data } = dialogState;
    const options = {
      url: `costings/${costing?.id}/miniMarkers`,
      method: "delete",
      params: { ...data }
    };
    mutate(options, {
      onSuccess: () => {
        setVersion(version + 1);
        setDialogState({});
        setIsAttachmentUpload(false);
        dispatch(showNotification("Mini Marker deleted successfully."));
      }
    });
  };

  const onPrint = () => {
    if (!costing) return;
    var doc = new jsPDF();

    setDefault(doc, "Costing Consumption Report", tenant, null, "");
    doc.setFontSize(11);

    const pageHeight = doc.internal.pageSize.getHeight();
    let startY = 30;
    const leftMargin = 7;

    const ensureSpace = requiredHeight => {
      if (pageHeight - (startY + 15) < requiredHeight) {
        doc.addPage();
        startY = 10;
      }
    };

    const headerStyle = [
      { dataKey: "cuChecklist", header: "CU Checklist" },
      { dataKey: "buyerName", header: "Buyer" },
      { dataKey: "productName", header: "Product" },
      { dataKey: "style", header: "Style Name" },
      { dataKey: "styleColor", header: "Style Color" },
      { dataKey: "styleDescription", header: "Style Description" },
      { dataKey: "fit", header: "Fit" }
    ];

    const rowStyle = [
      {
        cuChecklist: costing?.cstId || "-",
        buyerName: costing?.customer?.name || "-",
        productName: costing?.product?.name || "-",
        style: costing?.styles || "-",
        styleColor: costing?.colors || "-",
        styleDescription: costing?.styleDesc || "-",
        remarks: costing?.remarks || "-",
        fit: costing?.fitValue || "-"
      }
    ];

    doc.text("Style Details", leftMargin, startY + 3);
    doc.autoTable({
      startY: startY + 7,
      head: [headerStyle.map(c => c.header)],
      body: rowStyle.map(r => headerStyle.map(c => r[c.dataKey])),
      styles: { fontSize: 9, cellPadding: 2 },
      margin: { left: leftMargin, right: 7 }
    });

    startY = doc.autoTable.previous.finalY + 4;

    ensureSpace(minReqHeight);
    doc.text("Costing Summary", leftMargin, startY + 3);

    const costingColumns = [
      { header: "COLOR", dataKey: "color" },
      { header: "PART", dataKey: "part" },
      { header: "PLACEMENT", dataKey: "placement" },
      { header: "ARTICLE CODE", dataKey: "articleCode" },
      { header: "SUPPLIER", dataKey: "supplier" },
      { header: "CUTTABLE WIDTH (CM)", dataKey: "width" },
      { header: "CSV", dataKey: "csv" },
      { header: "SHRINKAGE", dataKey: "shrinkage" },
      { header: "MARKER CU", dataKey: "layCu" },
      { header: "WASTAGE %", dataKey: "wastage" },
      { header: "FINAL CU", dataKey: "costingCu" }
    ];

    const colorList = getDistinctValues(costing?.colors?.split(",").filter(e => !isEmpty(e)));

    let costingBody = [];
    if (typeof rowsPart !== "undefined" && rowsPart.length > 0) {
      costingBody = colorList?.flatMap(color => {
        return rowsPart.map(r => ({
          color: color || "-",
          part: r.part || "-",
          placement: r.placement || "-",
          articleCode: r.articleCode || "-",
          supplier: r.supplierName || "-",
          width: r.width || "",
          csv: r.csv || "",
          shrinkage: r.shrinkage || "",
          layCu: r.layCu || "",
          wastage: r.wastagePercent || r.wastage || "",
          costingCu: r.costingCu || ""
        }));
      });
    }

    doc.autoTable({
      startY: startY + 7,
      head: [costingColumns.map(c => c.header)],
      body: costingBody.map(r => costingColumns.map(c => r[c.dataKey])),
      styles: { fontSize: 9, cellPadding: 2 },
      margin: { left: leftMargin, right: 7 }
    });

    startY = doc.autoTable.previous.finalY;

    const matchColumns = [
      { header: "COLOR", dataKey: "color" },
      { header: "PART", dataKey: "part" },
      { header: "PLACEMENT", dataKey: "placement" },
      { header: "MATCHING PARAMETER", dataKey: "matchingParameter" },
      { header: "FAB CLASSIFICATION", dataKey: "classification" },
      { header: "FAB PATTERN", dataKey: "fabricPatternName" },
      { header: "REPEATS IN CM", dataKey: "repeat" },
      { header: "FAB CATEGORY", dataKey: "fabricCategoryName" },
      { header: "FAB COMPOSITION", dataKey: "composition" },
      { header: "WASH", dataKey: "wash" },
      { header: "FAB DIRECTION", dataKey: "fabDirection" }
    ];

    const matchBody = colorList?.flatMap(color => {
      return rowsPart.map(r => ({
        color: color || "-",
        part: r.part || "-",
        placement: r.placement || "-",
        matchingParameter: r.matchingParameter || "-",
        classification: r.fabricClassification || "-",
        fabricPatternName: r.fabricPattern?.name || "-",
        repeat: r.repeat || "-",
        fabricCategoryName: r.fabCategoryName || "-",
        composition: r.fabComposition || "-",
        wash: r.wash || "-",
        fabDirection: r.fabDirection || "-"
      }));
    });

    doc.autoTable({
      startY: startY + 7,
      head: [matchColumns.map(c => c.header)],
      body: matchBody.map(r => matchColumns.map(c => r[c.dataKey])),
      styles: { fontSize: 9, cellPadding: 2 },
      margin: { left: leftMargin, right: 7 }
    });

    startY = doc.autoTable.previous.finalY + 6;

    const colSizeBreakup = [
      {
        dataKey: "color",
        title: "Color",
        align: "center"
      },
      ...columnsSizeBreakup
    ];

    if (!isEmpty(colSizeBreakup) && !isEmpty(rowsSizeBreakup)) {
      ensureSpace(minReqHeight);
      doc.text("Size Breakup", leftMargin, startY + 3);

      doc.autoTable({
        startY: startY + 7,
        columns: colSizeBreakup,
        body: rowsSizeBreakup?.map(r => ({ ...r, color: costing?.colors })),
        styles: { fontSize: 9, cellPadding: 2 },
        margin: { left: leftMargin, right: 7 }
      });

      startY = doc.autoTable.previous.finalY + 6;
    }

    partList?.forEach(part => {
      const columns = [
        { dataKey: "markerNo", header: "M. NO." },
        ...sizeKeys.map(sKey => ({
          dataKey: getSizeKey(sKey),
          header: sKey.split("_")[1]
        })),
        { dataKey: "way", header: "Way" },
        { dataKey: "qtyPercent", header: "QUANTITY (%)" },
        { dataKey: "length", header: "Marker Length" },
        { dataKey: "efficiency", header: "EFFICIENCY" }
      ];

      const rows = (rowsMarker || []).filter(r => r.part === part);
      const title = `Markers${partList.length > 1 ? ` - ${part}` : ""}`;

      if (rows.length === 0) {
        ensureSpace(20);
        doc.text(`${part} Markers not available.`, leftMargin, startY + 7);
        startY += 12;
        return;
      }

      ensureSpace(minReqHeight);
      doc.text(title, leftMargin, startY + 3);

      const body = rows.map(r => columns.map(c => r?.[c.dataKey] ?? ""));

      doc.autoTable({
        startY: startY + 7,
        head: [columns.map(c => c.header)],
        body,
        styles: { fontSize: 9, cellPadding: 2 },
        margin: { left: leftMargin, right: 7 }
      });

      startY = (doc.autoTable.previous?.finalY || startY) + 6;
    });

    if (!isEmpty(columnsSummary) && !isEmpty(rowsSummaryMap)) {
      ensureSpace(minReqHeight);
      doc.text("Costing Comparison - Shell", leftMargin, startY + 3);
      doc.autoTable({
        startY: startY + 10,
        columns: columnsSummary,
        body: rowsSummaryMap[PART_SHELL],
        styles: { fontSize: 9, cellPadding: 2 },
        margin: { left: leftMargin, right: 7 }
      });
    }

    doc.setFontSize(9);
    doc.save(`Costing.pdf`);
  };

  const onDelete = () => {
    const options = {
      url: `costings/${id}`,
      method: "delete",
      params: { action: "revision", revNo: revisions[revTabIdx] },
      data: sanitizeCosting(costing)
    };

    mutate(options, {
      onSuccess: () => {
        setVersion(version => version + 1);
        setRevTabIdx(0);
        dispatch(showNotification(`Costing ${revisions[revTabIdx]} deleted successfully.`));
      }
    });
  };

  const onFinalizeSubmit = values => {
    if (isEmpty(values?.revisionNo)) {
      dispatch(showNotification(`Revision No is required for finalizing Costing`, { type: "danger" }));
      return;
    }

    const options = {
      url: `costings/${id}`,
      method: "put",
      params: { action: "finalize", revNo: values?.revisionNo },
      data: sanitizeCosting(costing)
    };

    mutate(options, {
      onSuccess: () => {
        setVersion(version => version + 1);
        onFinalizeDialogClose();
        dispatch(showNotification("Revision finalized successfully."));
      }
    });
  };

  return {
    revTabIdx,
    partIdx,
    costing,
    revisionCount,
    revisions,
    partList,
    columnsPart,
    rowsPart,
    columnsSizeBreakup,
    rowsSizeBreakup,
    columnsMarker,
    rowsMarker,
    columnsSummary,
    rowsSummaryMap,
    editing,
    reasonEditing,
    openFinalizeDialog,
    manuallyFinalizedRevNo,
    busy,
    dialogState,
    choicesTechSheets,
    choicesCostingReasons,
    choicesRevisions,
    onFooterActionClick,
    setRevTabIdx,
    setPartIdx,
    setEditing,
    setOpenFinalizeDialog,
    onFinalizeSubmit,
    setDialogState,
    onDetailsSubmit,
    onPartSubmit,
    onSizeBreakupSubmit,
    onMarkerSubmit,
    onRevisionCreate,
    onCostingReasonsSubmit,
    onReasonEditingClose,
    onFinalizeDialogClose,
    onDeleteMiniMarker,
    onDownloadTechSheet,
    onPrint,
    onDelete,
    isAttachmentUpload,
    setIsAttachmentUpload,
    attachColumnsMarker,
    drawerState,
    setDrawerState,
    onDrawerSubmit
  };
};

export default useCostingView;
